import { memo, type ReactNode, useCallback } from 'react';

import { type ShortUrlResponse } from '@air/api/types';

import { OnOffShareToggleRow } from '~/components/ShareModal/components/OnOffShareToggleRow';
import { type OnShortUrlUpdate } from '~/components/ShareModal/types/onShortUrlUpdate';
import { type ShareModalUpdateResponse } from '~/components/ShareModal/types/shareModalUpdateResponse';
import { shareUrlUpdatedHideAirLogo } from '~/utils/shortUrl/shareUrlUpdatedAnalytics';

export type ShareBrandingAirLogoSettingRowProps = {
  shortIdInfo: ShortUrlResponse;
  onShortUrlUpdate: OnShortUrlUpdate<ShareModalUpdateResponse>;
  disabled?: boolean;
  labelAccessory?: ReactNode;
};

export const ShareBrandingAirLogoSettingRow = memo(
  ({ shortIdInfo, onShortUrlUpdate, disabled, labelAccessory }: ShareBrandingAirLogoSettingRowProps) => {
    const handleToggle = useCallback(
      (hide: boolean) =>
        onShortUrlUpdate({
          props: { presentationSettings: { hideAirLogo: hide } },
          analytics: shareUrlUpdatedHideAirLogo(hide),
        }),
      [onShortUrlUpdate],
    );

    // Gating is caller-driven: callers apply the Enterprise plan gate via `disabled` and the
    // upsell affordance via `labelAccessory` (removing the Air logo is Enterprise-only).
    return (
      <OnOffShareToggleRow
        label="Hide Air logo"
        checked={shortIdInfo.presentationSettings?.hideAirLogo === true}
        disabled={disabled}
        labelAccessory={labelAccessory}
        data-testid="HIDE_AIR_LOGO_TOGGLE"
        onToggle={handleToggle}
      />
    );
  },
);

ShareBrandingAirLogoSettingRow.displayName = 'ShareBrandingAirLogoSettingRow';
